import { useState } from "react";
import getConfig from "src/analyticsProvider/index";
import { trackPageInteraction } from "asos-contentweb-analytics-middleware";
import updatePageNumberQueryParam from "src/utilities/updatePageNumberQueryParam";
import fetchFaqs from "src/utilities/fetchFaqs";
import noticeError from "src/app/utilities/noticeError";

const useFaqProvider = ({
  analytics,
  currentPage: initialCurrentPage,
  faqID,
  itemCount: faqTotal = "0",
  items: initialFaqs = [],
  pageSize,
  storeData,
}) => {
  const [faqs, setFaqs] = useState(initialFaqs);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [visitedPages, setVisitedPages] = useState([initialCurrentPage]);
  const [itemToFocus, setItemToFocus] = useState(null);

  const maxPageCount = Math.ceil(faqTotal / pageSize);
  const prevButtonIsActive = !visitedPages.includes(0) && currentPage + 1 > 1;
  const nextButtonIsActive =
    !visitedPages.includes(maxPageCount - 1) && currentPage + 1 < maxPageCount;

  const [fixedErrorVisible, setFixedErrorVisible] = useState(false);
  const [failedDirection, setFailedDirection] = useState("");

  const onNextButtonClick = async (event) => {
    event.preventDefault();
    const updatedPageNextPosition = currentPage + 1;
    await loadFaqs(
      updatedPageNextPosition * 12,
      updatedPageNextPosition,
      "next",
      updatedPageNextPosition + 1 // pages are 0 indexed
    );
  };

  const onPreviousButtonClick = async (event) => {
    event.preventDefault();
    const updatedPagePrevPosition = currentPage - 1;
    await loadFaqs(
      updatedPagePrevPosition * 12,
      updatedPagePrevPosition,
      "prev",
      currentPage === 0 ? 1 : currentPage
    );
  };

  const loadFaqs = async (
    faqIndex,
    pageNumber,
    buttonDirection,
    queryString
  ) => {
    setFixedErrorVisible(false);
    setFailedDirection("");
    setLoading(true);
    try {
      const response = await fetchFaqs(faqID, storeData, pageSize, faqIndex);
      const results =
        buttonDirection === "next"
          ? [...faqs, ...response.results]
          : [...response.results, ...faqs];
      setFaqs(results);
      setItemToFocus(
        results.findIndex((item) => item.title === response.results[0].title)
      );
      setCurrentPage(pageNumber);
      setVisitedPages([...visitedPages, currentPage]);
      updatePageNumberQueryParam(queryString);
      sendAnalytics(queryString);
    } catch (err) {
      setFixedErrorVisible(true);
      setFailedDirection(buttonDirection);
      noticeError(err);
    } finally {
      setLoading(false);
    }
  };

  const sendAnalytics = (newPage) => {
    const configName = "faq-list-load-new-page";
    const loadMoreConfig = getConfig(configName, {
      pageNumber: newPage + 1, // pages are 0 indexed but we don't want that for analytics
      category: analytics.category,
    });
    trackPageInteraction(loadMoreConfig);
  };

  return {
    faqs,
    currentPage,
    prevButtonIsActive,
    nextButtonIsActive,
    onPreviousButtonClick,
    onNextButtonClick,
    itemToFocus,
    loading,
    fixedErrorVisible,
    failedDirection,
  };
};

export default useFaqProvider;
